import { MenuItem, SvgIcon, Typography } from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@opengov/react-capital-assets';
import * as React from 'react';

import { NAV_COLOR_PALETTE } from '../../Components/styleConstants';
import { useSwitchMenuContext } from '../../Components/SwitchMenu/SwitchMenuContext';
import { useMenuContext } from '../../ContextState/useMenuContext';
import { MobileKeyboardNavContextProps, useMobileKeyboardNavContext } from '../Context/MobileKeyboardNavContext';

import { DATA_TEST } from '../../enums';

import { MobileLevelOneMenuItem } from './MobileLevelOneMenuItem';

interface IMobileLevelOneMenuProps {
  autoFocusFirstItem: boolean;
  showOnlySwitchMenu?: boolean;
  handleSwitchMenuItemClick(): void;
}
export const MobileLevelOneMenu: React.FC<IMobileLevelOneMenuProps> = ({
  showOnlySwitchMenu = false,
  autoFocusFirstItem,
  handleSwitchMenuItemClick
}) => {
  const { menuItems: levelOneMenuItems, disableFavorites, setShowFavoritesSubmenu } = useMenuContext();
  const {
    mobileFocusFavoritesMenuItemNext,
    mobileFocusSwitchMenuItemNext,
    firstMobileMenuItemRef,
    setMobileFocusFavoritesMenuItemNext
  } = useMobileKeyboardNavContext() as MobileKeyboardNavContextProps;
  const { menuButtonLabel: switchMenuButtonLabel, menuItems: switchMenuItems } = useSwitchMenuContext() ?? {};

  const showSwitchMenuItem = switchMenuButtonLabel && switchMenuItems?.length > 0;
  const switchMenuItemIsFirst = (showOnlySwitchMenu || levelOneMenuItems?.length === 0) && autoFocusFirstItem;

  const handleKeyDown = (e: React.KeyboardEvent, handleClick: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      // Prevent from bubbling up and triggering handleClose on PopperSubmenuContainer
      e.stopPropagation();
    }
    if (e.key === 'ArrowRight') {
      handleClick();
    }
  };

  return (
    <>
      {!disableFavorites && !showOnlySwitchMenu && (
        <MenuItem
          ref={autoFocusFirstItem ? firstMobileMenuItemRef : null}
          autoFocus={autoFocusFirstItem || mobileFocusFavoritesMenuItemNext}
          data-test={DATA_TEST.mobile_level_one_favorites}
          onClick={() => {
            setShowFavoritesSubmenu(true);
            setMobileFocusFavoritesMenuItemNext(true);
          }}
          onKeyDown={(e) =>
            handleKeyDown(e, () => {
              setShowFavoritesSubmenu(true);
              setMobileFocusFavoritesMenuItemNext(true);
            })
          }
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            margin: (theme) => `${theme.spacing(0.5)} 0`,
            px: 2,
            minHeight: '40px',
            width: '100%'
          }}
        >
          <Typography variant="h4" color={NAV_COLOR_PALETTE.primaryGray} textAlign="center">
            Favorites
          </Typography>
          <SvgIcon inheritViewBox component={ChevronRightIcon} sx={{ color: NAV_COLOR_PALETTE.primaryGray }} />
        </MenuItem>
      )}
      {!showOnlySwitchMenu &&
        levelOneMenuItems.map((item, index) => (
          <MobileLevelOneMenuItem
            key={item.id}
            menuItem={item}
            isFirstMobileMenuItem={disableFavorites && index === 0 && autoFocusFirstItem}
          />
        ))}
      {showSwitchMenuItem && (
        <MenuItem
          data-test={DATA_TEST.mobile_level_one_switch_menu}
          onClick={handleSwitchMenuItemClick}
          onKeyDown={(e) => handleKeyDown(e, handleSwitchMenuItemClick)}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            margin: (theme) => `${theme.spacing(0.5)} 0`,
            px: 2,
            minHeight: '40px',
            width: '100%'
          }}
          ref={switchMenuItemIsFirst ? firstMobileMenuItemRef : null}
          autoFocus={switchMenuItemIsFirst || mobileFocusSwitchMenuItemNext}
        >
          <Typography fontWeight="semiBold" textAlign="center">
            {switchMenuButtonLabel}
          </Typography>
          <SvgIcon inheritViewBox component={ChevronRightIcon} sx={{ color: NAV_COLOR_PALETTE.primaryGray }} />
        </MenuItem>
      )}
    </>
  );
};
